import {
  dataResponse,
  DeliveryLink,
  InviteUserRequest,
  InviteUserResponse,
  pagedResponse,
  SessionSummary,
  UpdateUserRequest,
  User,
  type ListUsersQuery,
} from '@horevia/contracts';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRequest } from '@/lib/api';

export type UsersFilters = Partial<
  Pick<z.infer<typeof ListUsersQuery>, 'q' | 'status' | 'roleCode'>
>;

export const userKeys = {
  all: ['users'] as const,
  list: (filters: UsersFilters) => ['users', 'list', filters] as const,
  detail: (id: string) => ['users', 'detail', id] as const,
  sessions: (id: string) => ['users', 'sessions', id] as const,
};

const UsersPage = pagedResponse(User);
const UserEnvelope = dataResponse(User);
const UserSessions = dataResponse(z.array(SessionSummary));
const InviteEnvelope = dataResponse(InviteUserResponse);
const DeliveryLinkEnvelope = dataResponse(DeliveryLink);
const RevokedCount = z.object({ revoked: z.number().int() });

/** Liste paginée par curseur : « Charger 25 de plus » (API_GUIDELINES §3). */
export const usersQueryOptions = (filters: UsersFilters, limit = 25) =>
  infiniteQueryOptions({
    queryKey: userKeys.list(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiRequest('/users', {
        schema: UsersPage,
        signal,
        query: {
          limit,
          cursor: pageParam,
          q: filters.q,
          status: filters.status,
          roleCode: filters.roleCode,
        },
      }),
    getNextPageParam: (lastPage) => lastPage.page.nextCursor ?? undefined,
  });

export const userQueryOptions = (id: string) =>
  queryOptions({
    queryKey: userKeys.detail(id),
    queryFn: async () => (await apiRequest(`/users/${id}`, { schema: UserEnvelope })).data,
  });

export const userSessionsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: userKeys.sessions(id),
    queryFn: async () => (await apiRequest(`/users/${id}/sessions`, { schema: UserSessions })).data,
  });

/**
 * 201 : le compte créé et son lien d'activation (`activation`), remis à l'écran
 * à l'administrateur (D-055) ; `emailSent` indique si un e-mail est aussi parti.
 */
export async function inviteUser(
  payload: z.infer<typeof InviteUserRequest>,
): Promise<InviteUserResponse> {
  return (await apiRequest('/users', { method: 'POST', body: payload, schema: InviteEnvelope }))
    .data;
}

export async function updateUser(id: string, payload: z.infer<typeof UpdateUserRequest>) {
  return (
    await apiRequest(`/users/${id}`, { method: 'PATCH', body: payload, schema: UserEnvelope })
  ).data;
}

/**
 * `POST /users/:id/reactivate` ou `POST /users/:id/deactivate` (users.controller.ts).
 * Ces routes ne prennent pas de corps : le statut n'est pas soumis au verrou optimiste.
 */
export async function setUserActive(id: string, active: boolean) {
  return (
    await apiRequest(`/users/${id}/${active ? 'reactivate' : 'deactivate'}`, {
      method: 'POST',
      schema: UserEnvelope,
    })
  ).data;
}

/**
 * 200 `{ data: DeliveryLink }` : nouveau lien d'activation (l'ancien jeton est
 * révoqué), remis à l'écran (D-055). 409 CONFLICT si le compte n'est plus INVITED.
 */
export async function resendInvitation(id: string): Promise<DeliveryLink> {
  return (
    await apiRequest(`/users/${id}/invitation/resend`, {
      method: 'POST',
      schema: DeliveryLinkEnvelope,
    })
  ).data;
}

/** 200 `{ data: DeliveryLink }` : lien de réinitialisation (30 min), remis à l'écran (D-055). */
export async function resetUserPassword(id: string): Promise<DeliveryLink> {
  return (
    await apiRequest(`/users/${id}/reset-password`, {
      method: 'POST',
      schema: DeliveryLinkEnvelope,
    })
  ).data;
}

/** 200 `{ revoked: n }` : nombre de sessions effectivement révoquées. */
export async function revokeUserSessions(id: string): Promise<number> {
  const result = await apiRequest(`/users/${id}/sessions/revoke`, {
    method: 'POST',
    schema: RevokedCount,
  });
  return result.revoked;
}
